import { datadogRum } from '@datadog/browser-rum';
import axios, {
  AxiosError,
  AxiosHeaders,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';

import {
  LOAN_AMOUNT_AND_PURPOSE_LOCAL_STORAGE_KEY,
  redirectTo,
  STATE_LOCAL_STORAGE_KEY,
} from '@consumer-frontend/nc-utils';

import { cookieJar } from './cookieJar';
import {
  accountHomeApiBaseUrl,
  apiEndpoints,
  loginServerBaseUrl,
  loginServerTenantID,
  uiRoutes,
} from './endpoints';

const grantType = 'refresh_token';

export type AxiosConfigT = InternalAxiosRequestConfig & { _retry?: boolean; };
export type ApiClientAppT = 'dashboard' | 'apply';
export type ApiClientModeT = 'authenticated' | 'unauthenticated';
export type ApiClientOptionsT = {
  app: ApiClientAppT;
  mode: ApiClientModeT;
  // Defaults to logOutAndRedirect (dashboard will redirect to login page).
  // Override for authenticated apply endpoints will redirect to borrowing needs.
  onSessionExpired?: () => void;
  withCredentials?: boolean;
};

// Login server client
// Can't use the default apiClient here because login server needs `withCredentials` to be false.
export const defaultLoginServerPostData = {
  grant_type: grantType,
};
export const loginServerAxiosConfig: AxiosConfigT = {
  baseURL: loginServerBaseUrl,
  headers: new AxiosHeaders({
    'Content-Type': 'application/json',
    'X-Login-Server-Tenant-ID': loginServerTenantID,
  }),
  withCredentials: false,
};
export const loginServerClient = axios.create(loginServerAxiosConfig);

// Interceptor helpers
export const noopSuccessHandler = <T = AxiosResponse>(resp: T) => Promise.resolve(resp);
export const noopErrorHandler = (err: AxiosError) => Promise.reject(err);

export const logOutAndRedirect = () => {
  cookieJar.expire();

  redirectTo(uiRoutes.legacyAH.logOut, true);
};

const applySessionExpired = () => {
  cookieJar.expire();
  localStorage.removeItem(LOAN_AMOUNT_AND_PURPOSE_LOCAL_STORAGE_KEY);
  localStorage.removeItem(STATE_LOCAL_STORAGE_KEY);

  redirectTo(uiRoutes.apply.borrowingNeeds);
};

export const refreshTokens = (
  onSessionExpired: () => void = logOutAndRedirect,
): Promise<AxiosResponse | AxiosError | Error> => {
  const currentToken = cookieJar.getRefreshToken();

  if (!currentToken && import.meta.env.VITE_MOCKING !== 'true') {
    datadogRum.addAction('forcedLogout.noRefreshToken', cookieJar.getDebugDump());
    onSessionExpired();

    return Promise.reject(new Error('No refresh token found'));
  }

  return loginServerClient
    .post(apiEndpoints.shared.token, {
      ...defaultLoginServerPostData,
      refresh_token: currentToken,
    })
    .then(
      (resp: AxiosResponse<{ access_token: string; refresh_token: string; }>) => {
        cookieJar.expire();
        cookieJar.saveTokens(resp.data.access_token, resp.data.refresh_token);

        return Promise.resolve(resp);
      },
      (err: AxiosError) => {
        datadogRum.addAction('forcedLogout.loginServerError', cookieJar.getDebugDump());
        console.warn('Error refreshing token:', err);

        return Promise.reject(err);
      },
    )
  ;
};

export const retryRequest = (
  client: AxiosInstance,
  originalRequest: AxiosConfigT,
): Promise<AxiosResponse | AxiosError> => {
  const newRequest = { ...originalRequest, _retry: true };

  return client.request(newRequest).then(
    (resp: AxiosResponse) => Promise.resolve(resp),
    (err: AxiosError) => Promise.reject(err),
  );
};

export const refreshAndRetry = (
  client: AxiosInstance,
  onSessionExpired: () => void = logOutAndRedirect,
) => (err: AxiosError): Promise<AxiosResponse | AxiosError> => {
  const originalRequest = err.config as AxiosConfigT;
  const status = err.status || err.response?.status;

  if (status && [401, 403].includes(status)) {
    // Retry if the request hasn't already been retried.
    if (!originalRequest._retry) {
      return refreshTokens(onSessionExpired).then(
        () => retryRequest(client, originalRequest),
        (refreshError: AxiosError) => {
          datadogRum.addAction(
            'forcedLogout.refreshError',
            { ...cookieJar.getDebugDump(), refreshError: JSON.stringify(refreshError, null, 2) },
          );
          onSessionExpired();

          return Promise.reject(refreshError);
        },
      );
    }

    // If the request has already been retried but we're still seeing 401/403 from the API, this means the token refresh
    // succeeded but the session is expired, so log out and redirect.
    datadogRum.addAction(
      'forcedLogout.sessionExpired',
      { ...cookieJar.getDebugDump(), originalRequest: JSON.stringify(originalRequest, null, 2) },
    );
    onSessionExpired();

    return Promise.reject(err);
  }

  return Promise.reject(err);
};

export const ensureFreshToken = (
  client: AxiosInstance,
  onSessionExpired: () => void = logOutAndRedirect,
) => (req: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
  // Always sync the current token from the cookie to handle cases where the client was
  // created before tokens existed (e.g. the apply app's authenticated client).
  req.headers.Authorization = `Bearer ${cookieJar.getAccessToken()}`;

  if (!cookieJar.isTokenExpired()) {
    return Promise.resolve(req);
  }

  return refreshTokens(onSessionExpired).then(
    () => {
      // eslint-disable-next-line no-param-reassign -- intentional: keeps client default header in sync after token refresh
      client.defaults.headers.Authorization = `Bearer ${cookieJar.getAccessToken()}`;
      req.headers.Authorization = `Bearer ${cookieJar.getAccessToken()}`;

      return Promise.resolve(req);
    },
    (err: AxiosError) => {
      datadogRum.addAction(
        'forcedLogout.ensureFreshTokenError',
        { ...cookieJar.getDebugDump(), error: JSON.stringify(err, null, 2) },
      );
      onSessionExpired();

      return Promise.reject(err);
    },
  );
};

// Factory that creates a configured Axios instance for a given app and auth mode.
// Authenticated clients include JWT auth headers, cookies, and token refresh interceptors.
// Unauthenticated clients omit auth headers.
export const createApiClient = ({
  app,
  mode,
  onSessionExpired = logOutAndRedirect,
  withCredentials = true,
}: ApiClientOptionsT): AxiosInstance => {
  const isAuthenticated = mode === 'authenticated';

  const headers = new AxiosHeaders({
    Accept: 'application/json, text/html, text/plain, */*',
    'Content-Type': 'application/json',
    'X-APP-NAME': app,
    ...(isAuthenticated && {
      Authorization: `Bearer ${cookieJar.getAccessToken()}`,
      ClientID: app,
    }),
  });

  const client = axios.create({
    baseURL: accountHomeApiBaseUrl,
    headers,
    withCredentials,
  });

  if (isAuthenticated) {
    client.interceptors.request.use(ensureFreshToken(client, onSessionExpired), noopErrorHandler);
    client.interceptors.response.use(noopSuccessHandler, refreshAndRetry(client, onSessionExpired));
  }

  return client;
};

// API clients
export const apiClient = createApiClient({ app: 'dashboard', mode: 'authenticated' });
export const unauthenticatedApplyApiClient = createApiClient({ app: 'apply', mode: 'unauthenticated' });
export const authenticatedApplyApiClient = createApiClient({
  app: 'apply',
  mode: 'authenticated',
  onSessionExpired: applySessionExpired,
});
